<script setup lang="ts">
import { Button } from '~ds/components/ui/button';
import { Heading } from '~ds/components/ui/typography';
import { useI18n } from 'vue-i18n';

import type { FeedbackSurveyQuestionLikert, LikertOptionValue } from './types';

import { feedbackQuestionLabel, LIKERT_I18N_KEYS } from './types';

const props = defineProps<{
  question: FeedbackSurveyQuestionLikert;
}>();

const model = defineModel<string | undefined>();
const { t } = useI18n();

const label = computed(() => feedbackQuestionLabel(props.question, t));

const toStoredValue = (enumValue: LikertOptionValue): string =>
  enumValue.toLowerCase();

const options = computed(() =>
  props.question.likertOptions.map(enumValue => ({
    value: toStoredValue(enumValue),
    labelKey: LIKERT_I18N_KEYS[enumValue],
  })),
);
</script>

<template>
  <div class="flex flex-col items-center gap-6">
    <Heading
      size="md"
      as="h3"
      class="text-center max-w-sm"
    >
      {{ label }}
    </Heading>
    <div
      class="flex flex-col gap-3 w-full max-w-xs"
      role="radiogroup"
      :aria-label="label"
    >
      <Button
        v-for="option in options"
        :key="option.value"
        type="button"
        role="radio"
        size="lg"
        :variant="model === option.value ? 'default' : 'outline'"
        :aria-checked="model === option.value"
        class="w-full font-semibold"
        :class="{ 'scale-105': model === option.value }"
        @click="model = option.value"
      >
        {{ t(option.labelKey) }}
      </Button>
    </div>
  </div>
</template>
